import React, { FC, memo } from 'react';
import {
  TApmPaymentTransitionResult,
  TCurrency,
  TSessionTransaction,
  TSessionTransactionReason,
} from '../../../../../api/src';
import { tableStyleConfig } from '../tableStyleConfig';
import {
  Badge,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Card, CardContent, ScrollBarWithFade } from '@payler/ui-components';
import { useTranslation } from 'react-i18next';
import { useCurrencyCallbacks } from '../../hooks/currencies-info';
import dayjs from 'dayjs';
import { TwoLevelTextTableCell } from '../TwoLevelTextTableCell';
import { ApmPaymentTooltip } from '../../components/ApmPaymentTooltip/ApmPaymentTooltip';

type TPaymentSessionTransitionTableProps = {
  currency: TCurrency;
  data: TSessionTransaction[];
};

type TTableBodyProps = TPaymentSessionTransitionTableProps;
type TTableRowProps = {
  currency: TCurrency;
  date: TSessionTransaction['date'];
  type: TSessionTransaction['type'];
  result: TSessionTransaction['result'];
  requestedAmount: TSessionTransaction['requestedAmount'];
  reason?: TSessionTransactionReason;
};
export const PaymentSessionTransitionTable: FC<TPaymentSessionTransitionTableProps> =
  memo(({ data, currency }) => {
    const showReason = data.some(
      (item) => !!item.errorDescription && !!item.groupErrorCode,
    );

    return (
      <Card
        w="full"
        borderLeftWidth={{ base: 0, sm: 0.5 }}
        borderRightWidth={{ base: 0, sm: 0.5 }}
      >
        <CardContent p={0}>
          <ScrollBarWithFade>
            <TableContainer position="relative">
              <Table styleConfig={tableStyleConfig}>
                <Header showReason={showReason} />
                <Body data={data} currency={currency} />
              </Table>
            </TableContainer>
          </ScrollBarWithFade>
        </CardContent>
      </Card>
    );
  });

const Header: FC<{ showReason: boolean }> = ({ showReason }) => {
  const { t } = useTranslation(['tableHeader']);
  return (
    <Thead>
      <Tr>
        <Th>{t('tableHeader:date')}</Th>
        <Th>{t('tableHeader:amount')}</Th>
        <Th>{t('tableHeader:type')}</Th>
        <Th>{t('tableHeader:result')}</Th>
        {showReason && <Th>{t('tableHeader:reason')}</Th>}
      </Tr>
    </Thead>
  );
};

const Body: FC<TTableBodyProps> = ({ data, currency }) => {
  const { getCurrencyUnitRate } = useCurrencyCallbacks();

  return (
    <Tbody>
      {data.map(
        (
          {
            result,
            type,
            requestedAmount,
            date,
            errorDescription,
            groupErrorCode,
          },
          i,
        ) => (
          <Row
            key={i}
            date={date}
            type={type}
            result={result}
            requestedAmount={
              (requestedAmount ?? 0) / getCurrencyUnitRate(currency)
            }
            currency={currency}
            reason={{ errorDescription, groupErrorCode }}
          />
        ),
      )}
    </Tbody>
  );
};

const Row: FC<TTableRowProps> = ({
  type,
  date,
  requestedAmount,
  result,
  currency,
  reason,
}) => {
  const resultBadgeStatus: Record<TApmPaymentTransitionResult, string> = {
    Pending: 'grey',
    Failed: 'red',
    Initiated: 'grey',
    Succeed: 'green',
  };
  const showReason = !!reason?.errorDescription && !!reason?.groupErrorCode;

  const tdStyles = {
    w: showReason ? '20%' : '25%',
    minW: '200px',
  };

  return (
    <Tr>
      <Td {...tdStyles}>
        <DateCell date={date} />
      </Td>
      <Td {...tdStyles}>
        <AmountCell amount={requestedAmount} currency={currency} />
      </Td>
      <Td {...tdStyles}>
        <Badge variant="grey">{type}</Badge>
      </Td>
      <Td {...tdStyles}>
        <Badge variant={resultBadgeStatus[result]}>{result}</Badge>
      </Td>
      {showReason && (
        <Td {...tdStyles}>
          <ReasonCell reason={reason} />
        </Td>
      )}
    </Tr>
  );
};

const DateCell: FC<{ date: string }> = ({ date }) => {
  const parsedDate = dayjs(date).format('DD.MM.YYYY');
  const parsedTime = dayjs(date).format('HH:mm:ss');

  return <TwoLevelTextTableCell top={parsedDate} bottom={parsedTime} />;
};

const AmountCell: FC<{ amount: number; currency: string }> = ({
  currency,
  amount,
}) => {
  return <TwoLevelTextTableCell top={amount} bottom={currency} />;
};

const ReasonCell: FC<{ reason: TSessionTransactionReason }> = ({ reason }) => {
  return (
    <Badge variant="red">
      <HStack spacing={1}>
        <Text whiteSpace="normal" wordBreak="break-word">
          {reason.errorDescription}
        </Text>
        <ApmPaymentTooltip label={`“${reason.groupErrorCode}”`} />
      </HStack>
    </Badge>
  );
};
